"use client";

import { db } from "../firebase";
import { collection, getDocs, orderBy, query, Timestamp } from "firebase/firestore";
import Link from "next/link";
import { useState, useEffect } from "react";
import { ClientBlogPost, ServerBlogPost } from "../types/BlogPost";

const placeholderImage = 'https://firebasestorage.googleapis.com/v0/b/enjoyabledesign-a7576.appspot.com/o/placeholders%2Fqi-bin-w4hbafegiac-unsplash.webp?alt=media&token=e49d8cff-0398-4e30-b5ab-d491a8488e44'; 

type DateColumnT = string | Timestamp;

function dateToString(date: DateColumnT) {
  if (typeof date == 'string') {
    return new Date(date).toLocaleDateString();
  }

  if (date instanceof Timestamp) {
    return date.toDate().toLocaleTimeString();
  }

  return new Date().toLocaleDateString();
}

export default function BlogPage() {
  const [blogs, setBlogs] = useState<ClientBlogPost[]>([]); // State to hold blogs
  const [filteredBlogs, setFilteredBlogs] = useState<ClientBlogPost[]>([]); // State for filtered blogs
  const [tags, setTags] = useState<string[]>([]); // State to hold all unique tags
  const [selectedTag, setSelectedTag] = useState<string | null>(null); // State for selected tag
  const [currentPage, setCurrentPage] = useState(1); // State for pagination
  const [loading, setLoading] = useState(true); // Loading state

  const pageSize = 4; // Number of blogs per page
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedBlogs = filteredBlogs.slice(startIndex, startIndex + pageSize);
  const totalPages = Math.ceil(filteredBlogs.length / pageSize);

  // Fetch blogs and tags on component mount
  useEffect(() => {
    async function fetchBlogs() {
      setLoading(true);
      const snapshot = await getDocs(
        query(collection(db, "Blog"), orderBy("Date", "desc"))
      );
      const fetchedBlogs: ClientBlogPost[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          Title: data.Title || "Untitled",
          shortDescription: data.shortDescription,
          Content: data.Content || "",
          slug: data.slug || "unknown-slug",
          Date: dateToString(data.Date),
          imageURL: data.imageURL || placeholderImage, 
          readingTime: data.readingTime || 0,
          tags: data.tags || [],
        };
      });

      // Set all blogs and extract unique tags
      setBlogs(fetchedBlogs);
      setFilteredBlogs(fetchedBlogs);
      
      // Extract unique tags
      const allTags = Array.from(new Set(fetchedBlogs.flatMap((blog) => blog.tags)));
      setTags(allTags);
      setLoading(false);
    }

    fetchBlogs();
  }, []);

  // Filter blogs by selected tag
  useEffect(() => {
    if (selectedTag) {
      setFilteredBlogs(blogs.filter((blog) => blog.tags.includes(selectedTag)));
    } else {
      setFilteredBlogs(blogs);
    }
  }, [selectedTag, blogs]);

  return (
    <div className="max-w-6xl mx-auto my-[35px]">
      <div className="flex gap-4 justify-center">
        <h2 className="text-[2.5rem] lg:text-[3.75rem] text-center text-blue-500">
          Enjoyable
        </h2>
        <h2 className="text-center text-[2.5rem] lg:text-[3.75rem]">Blog</h2>
      </div>

      {/* Tag Filter */}
      <div className="flex flex-wrap justify-center gap-3 mt-6 mb-6">
        <button
          onClick={() => setSelectedTag(null)} // Show all blogs when 'All' is clicked
          className={`text-md duration-300 text-gray-800 px-4 py-1 border border-gray-600 rounded-full mr-2 mb-2 hover:shadow-md transition-shadow cursor-pointer ${!selectedTag ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}
        >
          All
        </button>
        {tags.map((tag) => (
          <button
            key={tag}
            onClick={() => setSelectedTag(tag)}
            className={`text-md duration-300 text-gray-800 px-4 py-1 border border-gray-600 rounded-full mr-2 mb-2 hover:shadow-md transition-shadow cursor-pointer ${selectedTag === tag ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}
          >
            {tag}
          </button>
        ))}
      </div>

      {/* Show loading state */}
      {loading && <p>Loading...</p>}

      {/* Blog List */}
      {!loading && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
            {paginatedBlogs.map((blog, index) =>
              index === 0 ? (
                <div
                  key={blog.id}
                  className="col-span-1 lg:col-span-3 h-[400px] relative overflow-hidden rounded-[25px] shadow-lg group"
                  style={{
                    backgroundImage: `url(${blog.imageURL})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <Link href={`/blog/${blog.slug}`} className="absolute inset-0">
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t flex justify-between from-black to-transparent">
                      <div>
                        <h2 className="text-3xl font-bold text-white">
                          {blog.Title}
                        </h2>
                        <p className="text-gray-300 text-sm mt-2">
                          {blog.Date}
                        </p>
                        <p className="text-gray-200">
                          {blog.shortDescription}
                        </p>
                      </div>
                    </div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="35"
                      height="36"
                      viewBox="0 0 35 36"
                      fill="none"
                      className="absolute top-[10px] right-[10px] transition-all duration-700 -rotate-45 group-hover:rotate-0"
                    >
                      <rect
                        y="0.517578"
                        width="35"
                        height="35"
                        rx="17.5"
                        fill="white"
                      ></rect>
                      <path
                        d="M26.8563 18.0176L19.8391 11.0004M26.8563 18.0176L19.8391 25.0348M26.8563 18.0176L14.5762 18.0176M8.14373 18.0176L11.0676 18.0176"
                        stroke="#2C2D2C"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></path>
                    </svg>
                  </Link>
                </div>
              ) : (
                <Link
                  href={`/blog/${blog.slug}`}
                  key={blog.id}
                  className="col-span-1 h-[400px] border rounded-[25px] shadow-sm flex flex-col group transition-shadow duration-300 ease-in-out hover:shadow-md"
                >
                  {blog.imageURL && (
                    <img
                      src={blog.imageURL}
                      alt={blog.Title}
                      className="w-full aspect-video h-48 object-cover rounded-t-[25px] mb-4"
                    />
                  )}
                  <div className="px-4 flex flex-col justify-between flex-1">
                    <div>
                      <h2 className="text-xl font-semibold">{blog.Title}</h2>
                      <p className="text-gray-600 mb-4" >
                      {blog.shortDescription
                        ? blog.shortDescription.length > 50
                          ? blog.shortDescription.substring(0, 50) + "..."
                          : blog.shortDescription
                        : ""}
                      </p>
                    </div>
                    <div className="mt-auto flex items-center gap-2 mb-4">
                      <span className="text-blue-500">Read More</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 25"
                        fill="none"
                        className="transform group-hover:translate-x-1 transition-transform duration-300"
                      >
                        <path
                          d="M4 12.7598L20 12.7598M20 12.7598L14 18.7598M20 12.7598L14 6.75977"
                          stroke="#3b82f6"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        ></path>
                      </svg>
                    </div>
                  </div>
                </Link>
              )
            )}
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6 space-x-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`w-fit flex justify-end items-center gap-2.5 py-2 px-5 rounded-full border border-blue-500 text-blue-500 bg-blue-50 hover:bg-blue-500 hover:text-white transition duration-300 ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className={`w-fit flex justify-end items-center gap-2.5 py-2 px-5 rounded-full border border-blue-500 text-blue-500 bg-blue-50 hover:bg-blue-500 hover:text-white transition duration-300 ${currentPage === totalPages
                ? "opacity-50 cursor-not-allowed"
                : ""
                }`}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
